import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  InputAdornment,
  Box,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

const alignmentOptions = ["left", "center", "right"];
const geometryFieldConfigs = [
  { key: "x", label: "Left", aliases: ["x", "X", "left"], decimals: 5 },
  { key: "width", label: "Width", aliases: ["width", "W"], decimals: 5 },
  { key: "y", label: "Top", aliases: ["y", "Y", "top"], decimals: 5 },
  { key: "height", label: "Height", aliases: ["height", "H"], decimals: 5 },
];

const getValue = (field, ...keys) => {
  for (const key of keys) {
    if (field?.[key] !== undefined && field?.[key] !== null) {
      return field[key];
    }
  }
  return "";
};

const formatNumber = (value, decimals = 5, integerOnly = false) => {
  if (
    value === "" ||
    value === null ||
    value === undefined ||
    Number.isNaN(Number(value))
  ) {
    return "";
  }

  const numericValue = Number(value);

  if (integerOnly) {
    return String(Math.round(numericValue));
  }

  return numericValue
    .toFixed(decimals)
    .replace(/\.0+$/, "")
    .replace(/(\.\d*?)0+$/, "$1");
};

const sectionSx = {
  boxShadow: "none",
  border: "1px solid #e8eaed",
  borderRadius: "10px !important",
  "&:before": { display: "none" },
};

const ObjectInspectorDrawer = ({
  isOpen,
  selectedField,
  selectedTextItem,
  pdfTextEdits,
  currentDocument,
  onFieldChange,
  loading,
}) => {
  const [expandedSections, setExpandedSections] = React.useState({
    geometry: true,
    general: true,
    appearance: true,
  });
  const [geometryDrafts, setGeometryDrafts] = React.useState({});

  React.useEffect(() => {
    if (!selectedField) {
      setGeometryDrafts({});
      return;
    }

    const nextGeometryDrafts = geometryFieldConfigs.reduce(
      (acc, fieldConfig) => {
        const rawValue = getValue(selectedField, ...fieldConfig.aliases);
        const displayValue = rawValue === "" ? "" : Number(rawValue);

        acc[fieldConfig.key] = formatNumber(displayValue, fieldConfig.decimals);
        return acc;
      },
      {},
    );

    setGeometryDrafts(nextGeometryDrafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedField]);

  const handleSectionToggle = (sectionKey) => (_, expanded) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: expanded,
    }));
  };

  const handleChange = (fieldKey, parser) => (event) => {
    const rawValue = event.target.value;
    const nextValue = parser ? parser(rawValue) : rawValue;
    onFieldChange?.(fieldKey, nextValue);
  };

  const commitGeometryValue = (fieldKey, aliases, decimals = 5) => {
    const draftValue = geometryDrafts[fieldKey];

    if (draftValue === "") {
      onFieldChange?.(fieldKey, "");
      return;
    }

    const parsedValue = Number(draftValue);
    if (!Number.isFinite(parsedValue)) {
      const originalValue = getValue(selectedField, ...aliases);
      const displayValue = originalValue === "" ? "" : Number(originalValue);
      setGeometryDrafts((prev) => ({
        ...prev,
        [fieldKey]: formatNumber(displayValue, decimals),
      }));
      return;
    }

    onFieldChange?.(fieldKey, parsedValue);
    setGeometryDrafts((prev) => ({
      ...prev,
      [fieldKey]: formatNumber(parsedValue, decimals),
    }));
  };

  const selectedTextEdit = React.useMemo(() => {
    if (!selectedTextItem) return null;
    return (pdfTextEdits || []).find(
      (edit) => edit.target_item_id === selectedTextItem.id,
    );
  }, [pdfTextEdits, selectedTextItem]);

  return (
    <Box
      sx={{
        width: isOpen ? "20%" : 0,
        minWidth: isOpen ? 280 : 0,
        maxWidth: isOpen ? 360 : 0,
        height: "100%",
        transition: "all 260ms ease",
        overflow: "hidden",
        borderLeft: isOpen ? "1px solid #e0e0e0" : "none",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid #f0f0f0" }}>
        <Typography variant="subtitle1" align="center" sx={{ fontWeight: 700 }}>
          {selectedTextItem
            ? "PDF Text"
            : selectedField && selectedField?.type === "PDFTextField"
              ? "Text Field"
              : selectedField && selectedField?.type === "PDFCheckBox"
                ? "Check Box"
                : "Object Inspector"}
        </Typography>
      </Box>

      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          overflowY: "auto",
          minHeight: 0,
          flex: 1,
        }}
      >
        {selectedTextItem ? (
          <>
            <Accordion
              expanded={expandedSections.geometry}
              onChange={handleSectionToggle("geometry")}
              disableGutters
              sx={sectionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  PDF Text Geometry
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.25,
                  }}
                >
                  {[
                    [
                      "x",
                      selectedTextEdit?.rect?.X ?? selectedTextItem?.rect?.X,
                    ],
                    [
                      "width",
                      selectedTextEdit?.rect?.W ?? selectedTextItem?.rect?.W,
                    ],
                    [
                      "y",
                      selectedTextEdit?.rect?.Y ?? selectedTextItem?.rect?.Y,
                    ],
                    [
                      "height",
                      selectedTextEdit?.rect?.H ?? selectedTextItem?.rect?.H,
                    ],
                  ].map(([key, value]) => (
                    <TextField
                      key={key}
                      label={key}
                      disabled={loading}
                      size="small"
                      type="number"
                      value={formatNumber(value, 5)}
                      onChange={(event) =>
                        onFieldChange?.(key, Number(event.target.value))
                      }
                      inputProps={{ step: 0.0001 }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSections.general}
              onChange={handleSectionToggle("general")}
              disableGutters
              sx={sectionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>PDF Text</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
                >
                  <TextField
                    label="Original Text"
                    size="small"
                    value={selectedTextItem?.text || ""}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Replacement Text"
                    size="small"
                    disabled={loading}
                    multiline
                    minRows={2}
                    value={
                      selectedTextEdit?.new_text ?? selectedTextItem?.text ?? ""
                    }
                    onChange={(event) =>
                      onFieldChange?.("text", event.target.value)
                    }
                  />
                  <TextField
                    label="Action"
                    size="small"
                    disabled={loading}
                    select
                    value={selectedTextEdit?.action || "replace"}
                    onChange={(event) =>
                      onFieldChange?.("action", event.target.value)
                    }
                  >
                    <MenuItem value="replace">replace</MenuItem>
                    <MenuItem value="move">move</MenuItem>
                    <MenuItem value="delete">delete</MenuItem>
                  </TextField>
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        ) : selectedField ? (
          <>
            <Accordion
              expanded={expandedSections.geometry}
              onChange={handleSectionToggle("geometry")}
              disableGutters
              sx={sectionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Geometry</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.25,
                  }}
                >
                  {geometryFieldConfigs.map((fieldConfig) => (
                    <TextField
                      key={fieldConfig.key}
                      label={fieldConfig.label}
                      size="small"
                      type="number"
                      disabled={loading}
                      value={geometryDrafts[fieldConfig.key] ?? ""}
                      onChange={(event) => {
                        setGeometryDrafts((prev) => ({
                          ...prev,
                          [fieldConfig.key]: event.target.value,
                        }));
                      }}
                      onBlur={() =>
                        commitGeometryValue(
                          fieldConfig.key,
                          fieldConfig.aliases,
                          fieldConfig.decimals,
                        )
                      }
                      inputProps={{ step: 0.0001 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">pt</InputAdornment>
                        ),
                      }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSections.general}
              onChange={handleSectionToggle("general")}
              disableGutters
              sx={sectionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>General</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
                >
                  <TextField
                    label="Name"
                    size="small"
                    value={getValue(selectedField, "name")}
                    onChange={handleChange("name")}
                    disabled={loading}
                  />
                  <TextField
                    label="Label"
                    size="small"
                    disabled={loading}
                    value={getValue(
                      selectedField,
                      "field_label",
                      "label",
                      "placeholder",
                      "name",
                    )}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      onFieldChange?.("field_label", nextValue);
                      onFieldChange?.("label", nextValue);
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSections.appearance}
              onChange={handleSectionToggle("appearance")}
              disableGutters
              sx={sectionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Appearance</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
                >
                  <TextField
                    label="Line Width"
                    size="small"
                    type="number"
                    disabled={
                      (!selectedField?.outline &&
                        !["PDFRectangle", "PDFLine"].includes(
                          selectedField?.type,
                        )) ||
                      loading
                    }
                    value={
                      getValue(selectedField, "lineWidth", "alignment") || 1
                    }
                    onChange={(event) =>
                      onFieldChange?.("lineWidth", Number(event.target.value))
                    }
                  />

                  <TextField
                    label="Alignment"
                    size="small"
                    disabled={loading}
                    select
                    value={
                      getValue(selectedField, "textAlign", "alignment") ||
                      "left"
                    }
                    onChange={handleChange("textAlign")}
                  >
                    {alignmentOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Select a field on the canvas to inspect its details.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ObjectInspectorDrawer;
